import { container } from '@/container';
import { GenerationsManager } from '@/managers/GenerationsManager';
import { normalize } from '@/utils/normalize';

function isNumericId(value: string): boolean {
  return /^\d+$/.test(value);
}

function resolveGenerationSuffix(generation?: string | null): string {
  if (!generation || generation === '0') {
    return '';
  }

  // Some calls pass the generation *label* (e.g. 'I', 'Jr.') rather than an id.
  if (!isNumericId(generation)) {
    return generation;
  }

  try {
    const generationManager = container.get(GenerationsManager);
    const item = generationManager?.find?.(generation);
    const label = typeof (item as unknown as { label?: unknown } | undefined)?.label === 'string'
      ? (item as unknown as { label: string }).label
      : undefined;
    const value = typeof (item as unknown as { value?: unknown } | undefined)?.value === 'string'
      ? (item as unknown as { value: string }).value
      : undefined;
    return label ?? value ?? '';
  }
  catch {
    // In test environments the DI container may not be configured.
    return '';
  }
}

export interface Name {
  first?: string;
  firstName?: string;
  last?: string;
  lastName?: string;
  middle?: string | null;
  middleName?: string | null;
  middleNames?: string | null;
  generation?: string | null;
  fallbackText?: string | null;
}
/**
 * Format the name
 * @param {object} "{first, last, middle, generation, fallbackText = ''}" needs to be formatted
 * @returns formatted name
 */
export function formatName(name: Name) {
  const normalized = normalize(name);
  let formattedName: string;
  const firstName = normalized.firstName || normalized.first;
  const lastName = normalized.lastName || normalized.last;
  const middleName = normalized.middleName || normalized.middle || normalized.middleNames;
  const { generation } = normalized;
  const fallbackText = normalized.fallbackText || '';

  if (generation !== '0') {
    const generationLabel = resolveGenerationSuffix(generation);
    formattedName = [lastName, generationLabel].filter((part) => !!part).join(' ');
    formattedName = [formattedName, firstName].filter((part) => !!part).join(', ') || fallbackText;
  }
  else {
    formattedName = [lastName, firstName].filter((part) => !!part).join(', ') || fallbackText;
  }

  if (middleName && middleName !== ' ') {
    formattedName = `${formattedName} ${middleName}.`;
  }

  return formattedName ?? fallbackText;
}
